import React, { useEffect, useState } from "react";
import {
  Card,
  Grid,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { useTranslation } from "react-i18next";

import TextInput from "./TextInput";
import {
  TaxFilingStatuses,
  States,
  TaxDeductionPreferences,
} from "../constants";
import TaxOptionsTable from "./TaxOptionsTable";

const currYear = new Date().getFullYear();

const TaxesTab = ({
  updateTaxes,
  data,
  remainingYears,
  changed,
  taxDeductions,
  taxes,
}) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState(null);
  const [filingStatus, setFilingStatus] = useState(data.taxFilingStatusType);
  const [taxDeductionPref, setTaxDeductionPref] = useState(data.deductionType);
  const [deductionAmount, setDeductionAmount] = useState(
    data.itemizedDeductionAmount
  );
  const [retireState, setRetireState] = useState(
    data.retirementStateAbbreviation
  );

  const YEARS = Array.from(Array(remainingYears).keys(), (x) => currYear + x);

  useEffect(() => {
    const payload = {
      ...taxes,
      taxFilingStatusType: filingStatus,
      deductionType: taxDeductionPref,
      itemizedDeductionAmount: deductionAmount,
      retirementStateAbbreviation: retireState,
    };

    updateTaxes(payload);
  }, [filingStatus, taxDeductionPref, deductionAmount, retireState]);

  const taxDeductionEntry = taxDeductions.find(
    (item) => item.taxFilingStatusKey === filingStatus
  );

  const isDeductionAmountValid =
    deductionAmount >= taxDeductionEntry.itemizedDeduction;

  const handleFilingStatusChange = (e) => {
    setFilingStatus(e.target.value);
    changed();
  };

  const handleRetireStateChange = (e) => {
    setRetireState(e.target.value);
    changed();
  };

  const handleTaxDeductionPrefChange = (e) => {
    setTaxDeductionPref(e.target.value);
    changed();
  };

  const handleDeductionAmountChange = (e) => {
    setDeductionAmount(parseFloat(e.target.value));
    changed();
  };

  return (
    <Grid
      container
      id="taxes-tab"
      className="tab-container"
      direction="column"
      md={9}
    >
      <Grid item>
        <Typography component="h5">
          Let's find out what types of taxes your client pay
        </Typography>
      </Grid>
      <Grid item>
        <Card className="card">
          <Grid container spacing={4} justifyContent="space-between">
            <Grid item md={12}>
              <Typography className="title">
                What is the client's tax filing status?
              </Typography>
            </Grid>
            <Grid item md={4} xs={12}>
              <FormControl fullWidth variant="outlined">
                <InputLabel id="tax-filing-status-select-label">
                  Filing Status
                </InputLabel>
                <Select
                  labelId="tax-filing-status-select-label"
                  id="tax-filing-status-select"
                  value={filingStatus}
                  label="Filing status"
                  onChange={handleFilingStatusChange}
                >
                  {Object.keys(TaxFilingStatuses).map((key) => (
                    <MenuItem key={key} value={key}>
                      {TaxFilingStatuses[key].label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Card>
        <Card className="card">
          <Grid container spacing={4}>
            <Grid item md={12}>
              <Typography className="title">
                What is the client's tax deduction preference?
              </Typography>
            </Grid>
            <Grid item md={4} xs={12}>
              <FormControl fullWidth variant="outlined">
                <InputLabel id="tax-deduction-preference-select-label">
                  Deduction Preference
                </InputLabel>
                <Select
                  labelId="tax-deduction-preference-select-label"
                  id="tax-deduction-preference-select"
                  value={taxDeductionPref}
                  label="Deduction Preference"
                  onChange={handleTaxDeductionPrefChange}
                >
                  {Object.keys(TaxDeductionPreferences).map((key) => (
                    <MenuItem key={key} value={key}>
                      {TaxDeductionPreferences[key]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {taxDeductionPref === "ITEMIZED" && (
              <Grid item md={4} xs={12}>
                <TextInput
                  id="input-deduction-amount-value"
                  type="number"
                  label="Deduction Amount"
                  value={deductionAmount}
                  adornment="$"
                  onChange={handleDeductionAmountChange}
                  error={!isDeductionAmountValid}
                  helperText="Must be greater than or equal to the Plan Setting Itemized Deduction Amount"
                  isRequired
                />
              </Grid>
            )}
          </Grid>
        </Card>
        <Card className="card">
          <Grid container spacing={4} alignItems="center">
            <Grid item md={12}>
              <Typography className="title">
                What state does the client want to retire in?
              </Typography>
            </Grid>
            <Grid item md={4} xs={12} className="mr-25">
              <FormControl fullWidth variant="outlined">
                <InputLabel id="state-select-label">State</InputLabel>
                <Select
                  labelId="state-select-label"
                  id="state-select"
                  value={retireState}
                  label="State"
                  required
                  onChange={handleRetireStateChange}
                >
                  {Object.keys(States).map((key) => (
                    <MenuItem key={key} value={key}>
                      {States[key]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Card>
        <Card className="card">
          <Grid container spacing={4}>
            <TaxOptionsTable taxes={taxes} updateTaxes={updateTaxes} />
          </Grid>
        </Card>
      </Grid>
    </Grid>
  );
};

export default TaxesTab;
